import {
  calendarYmdInZone,
  effectiveUserTimeZone,
  extractYmdFromDue,
  ymdDayDiff,
} from "./userTime";

/**
 * Work Item Styling Utilities
 *
 * Single source of truth for domain colors, urgency colors, and icons.
 *
 * Color Strategy:
 * - Domain colors (left border): Blue, Purple, Cyan, Pink, Indigo, Slate
 * - Urgency colors (full border): Red, Orange, Yellow, Green, Gray
 * - No overlap between the two palettes
 */

export type Domain =
  | "job_hunt"
  | "finance"
  | "business"
  | "parenthood"
  | "learning"
  | "personal"
  | "health"
  | "general";

export interface DomainConfigLike {
  key: string;
  label?: string | null;
  icon?: string | null;
  color?: string | null;
}

type DomainMeta = { leftBorderClass: string; icon: string; label: string; color: string; gradient: string };

export type Urgency = "overdue" | "today" | "week" | "later" | "none";

function normalizeDomainKey(domainRaw?: string | null) {
  let normalized = (domainRaw ?? "general").toLowerCase().trim();
  if (normalized === 'job_hunt' || normalized === 'job search' || normalized === 'application' || normalized === 'applied') {
    normalized = 'job_hunt';
  }
  if (normalized === 'work') {
    normalized = 'business';
  }
  return normalized;
}

const BASE_DOMAIN_MAP: Record<Domain, DomainMeta> = {
  job_hunt: {
    leftBorderClass: "border-l-blue-500",
    icon: "💼",
    label: "Job Search",
    color: "#3b82f6",
    gradient: "from-blue-600/20 to-transparent"
  },
  finance: {
    leftBorderClass: "border-l-emerald-500",
    icon: "💰",
    label: "Finance",
    color: "#10b981",
    gradient: "from-emerald-600/20 to-transparent"
  },
  business: {
    leftBorderClass: "border-l-amber-500",
    icon: "🏢",
    label: "Business",
    color: "#f59e0b",
    gradient: "from-amber-600/20 to-transparent"
  },
  parenthood: {
    leftBorderClass: "border-l-rose-500",
    icon: "🧸",
    label: "Parenthood",
    color: "#f43f5e",
    gradient: "from-rose-600/20 to-transparent"
  },
  learning: {
    leftBorderClass: "border-l-cyan-500",
    icon: "📚",
    label: "Learning",
    color: "#06b6d4",
    gradient: "from-cyan-600/20 to-transparent"
  },
  personal: {
    leftBorderClass: "border-l-fuchsia-500",
    icon: "👤",
    label: "Personal",
    color: "#d946ef",
    gradient: "from-fuchsia-600/20 to-transparent"
  },
  health: {
    leftBorderClass: "border-l-indigo-500",
    icon: "🏥",
    label: "Health",
    color: "#6366f1",
    gradient: "from-indigo-600/20 to-transparent"
  },
  general: {
    leftBorderClass: "border-l-slate-500",
    icon: "🌐",
    label: "General",
    color: "#64748b",
    gradient: "from-slate-600/20 to-transparent"
  },
};

/**
 * Get domain metadata (color, icon, label)
 */
export function getDomainMeta(domainRaw?: string | null, domainConfigs?: DomainConfigLike[]) {
  const key = normalizeDomainKey(domainRaw);
  const base = (BASE_DOMAIN_MAP as Record<string, DomainMeta>)[key] ?? BASE_DOMAIN_MAP.general;

  if (!domainConfigs || domainConfigs.length === 0) return base;

  const cfg = domainConfigs.find((d) => normalizeDomainKey(d.key) === key);
  if (!cfg) return base;

  const iconFromCfg = (cfg.icon ?? '').trim();
  const labelFromCfg = (cfg.label ?? '').trim();
  const colorFromCfg = (cfg.color ?? '').trim();

  const isKnownBase = Object.prototype.hasOwnProperty.call(BASE_DOMAIN_MAP, key);
  const iconFallback = isKnownBase ? base.icon : '✨';

  return {
    ...base,
    icon: iconFromCfg || iconFallback,
    label: labelFromCfg || (isKnownBase ? base.label : key),
    color: colorFromCfg || base.color,
  };
}

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * End of current week (through end of Sunday in local time)
 * Adjust if your app defines week differently
 */
function endOfWeek(d: Date) {
  const x = startOfDay(d);
  const day = x.getDay(); // 0=Sun, 6=Sat
  const diff = (7 - day) % 7; // days until Sunday
  x.setDate(x.getDate() + diff);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * Get urgency metadata based on due date (calendar day in user TZ when provided).
 */
export function getUrgencyMeta(
  dueDateRaw?: string | null,
  userTimeZone?: string | null,
  todayYmd?: string | null,
) {
  if (!dueDateRaw) {
    return {
      urgency: "none" as Urgency,
      borderClass: "border-gray-600",
      glowClass: "",
      label: "No due date",
      color: "#6b7280"
    };
  }

  const dueYmd = extractYmdFromDue(dueDateRaw);

  if (dueYmd) {
    const resolvedTodayYmd =
      todayYmd ?? calendarYmdInZone(new Date(), effectiveUserTimeZone(userTimeZone));
    const delta = ymdDayDiff(dueYmd, resolvedTodayYmd);
    if (Number.isNaN(delta)) {
      return {
        urgency: "none" as Urgency,
        borderClass: "border-gray-600",
        glowClass: "",
        label: "No due date",
        color: "#6b7280"
      };
    }
    if (delta < 0) {
      return {
        urgency: "overdue" as Urgency,
        borderClass: "border-red-500",
        glowClass: "shadow-[0_0_15px_rgba(239,68,68,0.4)]",
        label: "Overdue",
        color: "#ef4444"
      };
    }
    if (delta === 0) {
      return {
        urgency: "today" as Urgency,
        borderClass: "border-orange-500",
        glowClass: "shadow-[0_0_10px_rgba(249,115,22,0.3)]",
        label: "Due today",
        color: "#f97316"
      };
    }
    if (delta <= 7) {
      return {
        urgency: "week" as Urgency,
        borderClass: "border-yellow-500",
        glowClass: "",
        label: "Due this week",
        color: "#eab308"
      };
    }
    return {
      urgency: "later" as Urgency,
      borderClass: "border-green-500",
      glowClass: "",
      label: "Due later",
      color: "#22c55e"
    };
  }

  const due = new Date(dueDateRaw);
  if (Number.isNaN(due.getTime())) {
    return {
      urgency: "none" as Urgency,
      borderClass: "border-gray-600",
      glowClass: "",
      label: "No due date",
      color: "#6b7280"
    };
  }

  const now = new Date();
  const todayStart = startOfDay(now);
  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);

  if (due < todayStart) {
    return {
      urgency: "overdue" as Urgency,
      borderClass: "border-red-500",
      glowClass: "shadow-[0_0_15px_rgba(239,68,68,0.4)]",
      label: "Overdue",
      color: "#ef4444"
    };
  }

  if (due >= todayStart && due < tomorrowStart) {
    return {
      urgency: "today" as Urgency,
      borderClass: "border-orange-500",
      glowClass: "shadow-[0_0_10px_rgba(249,115,22,0.3)]",
      label: "Due today",
      color: "#f97316"
    };
  }

  if (due <= endOfWeek(now)) {
    return {
      urgency: "week" as Urgency,
      borderClass: "border-yellow-500",
      glowClass: "",
      label: "Due this week",
      color: "#eab308"
    };
  }

  return {
    urgency: "later" as Urgency,
    borderClass: "border-green-500",
    glowClass: "",
    label: "Due later",
    color: "#22c55e"
  };
}

/**
 * Get combined styling for a work item card
 * Returns classes for domain (left border) + urgency (full border)
 */
export function getWorkItemCardClasses(
  item: { domain?: string | null; due_date?: string | null },
  domainConfigs?: DomainConfigLike[],
  options?: { userTimeZone?: string | null; todayYmd?: string | null }
) {
  const domainMeta = getDomainMeta(item.domain, domainConfigs);
  const urgencyMeta = getUrgencyMeta(item.due_date, options?.userTimeZone, options?.todayYmd);

  return {
    domain: domainMeta,
    urgency: urgencyMeta,
    // Combined border classes
    borderClasses: `border-2 border-l-4 ${urgencyMeta.borderClass} ${domainMeta.leftBorderClass} ${urgencyMeta.glowClass}`,
  };
}
